import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiError, imgSrc } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, Loading, PageHeader } from "@/components/Shared";
import { RecordLayout } from "@/components/Record";
import { RecordTabs } from "@/components/Activities";
import { formatINR } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "@/components/ui/sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ArrowLeft, Package, Camera, Trash2, Loader2, Pencil, Plus, Boxes, FileText } from "lucide-react";
import ProductInventory from "@/components/inventory/ProductInventory";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [edit, setEdit] = useState(false);
  const [tab, setTab] = useState("inventory");
  const fileRef = useRef();
  const canEdit = user?.is_super_admin || ["Owner", "Manager"].includes(user?.role);
  const load = useCallback(() => api.get(`/products/${id}`).then(({ data }) => setP(data)), [id]);
  useEffect(() => { load(); }, [load]);
  if (!p) return <Loading />;

  const upload = async (file) => {
    const fd = new FormData(); fd.append("file", file); setUploading(true);
    try { await api.post(`/products/${id}/image`, fd); toast.success("Image uploaded"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setUploading(false); }
  };
  const removeImage = async () => { await api.delete(`/products/${id}/image`); toast.success("Image removed"); load(); };
  const margin = p.list_price - (p.purchase_price || 0);
  const src = imgSrc(p.image_url);

  return (
    <div data-testid="product-detail">
      <Button variant="ghost" className="mb-2 -ml-2 text-[#6B7189]" onClick={() => navigate("/products")}>
        <ArrowLeft className="w-4 h-4" /> Products
      </Button>
      <PageHeader title={p.name} subtitle={`${p.code} · ${p.brand || "—"}`}
        actions={<>{canEdit && <Button variant="outline" className="h-10" onClick={() => setEdit(true)} data-testid="product-edit-btn"><Pencil className="w-4 h-4" /> Edit</Button>}<Badge className="bg-[#101D51]">{p.category}</Badge></>} />

      <RecordLayout
        left={
          <Card className="p-5" data-testid="product-detail-left">
            <div className="relative mb-4 rounded-xl bg-[#F5F6FA] border border-[#E6E8F0] aspect-square flex items-center justify-center overflow-hidden" data-testid="product-image-box">
              {src ? <img src={src} alt={p.name} className="w-full h-full object-contain" data-testid="product-image" /> : <Package className="w-12 h-12 text-[#C7CBDA]" />}
              {canEdit && (
                <div className="absolute bottom-2 right-2 flex gap-1">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} data-testid="product-image-input" />
                  <Button size="sm" className="h-8 bg-[#101D51]" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="product-image-upload-btn">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Camera className="w-4 h-4" />} {src ? "Replace" : "Add photo"}</Button>
                  {src && <Button size="sm" variant="outline" className="h-8 bg-white" onClick={removeImage} data-testid="product-image-remove-btn"><Trash2 className="w-4 h-4 text-[#F83A84]" /></Button>}
                </div>)}
            </div>
            <Row k="Unit" v={p.unit} />
            <Row k="HSN" v={p.hsn || "—"} />
            <Row k="Barcode" v={p.barcode || "—"} mono />
            <Row k="GST rate" v={`${Math.round(p.gst_rate * 100)}%`} />
            <Row k="Purchase price" v={p.purchase_price ? formatINR(p.purchase_price) : "—"} />
            <Row k="List price" v={formatINR(p.list_price)} strong />
            <Row k="Margin" v={p.purchase_price ? `${formatINR(margin)} (${((margin / p.list_price) * 100).toFixed(1)}%)` : "—"} green={p.purchase_price > 0} />
            <Row k="Minimum price" v={formatINR(p.minimum_price)} />
            <Row k="Status" v={p.active ? "Active" : "Inactive"} />
          </Card>}
        right={<>
          <RecordTabs tabs={[{ id: "inventory", label: "Inventory", icon: Boxes }, { id: "pricebooks", label: "Price books", icon: FileText }]} active={tab} onChange={setTab} testid="product-tabs" />
          {tab === "inventory" ? <ProductInventory productId={id} product={p} canEdit={canEdit} />
            : <Card className="p-5"><PriceBooksPanel p={p} canEdit={canEdit} reload={load} /></Card>}
        </>} />
      {edit && <EditDialog p={p} onClose={() => setEdit(false)} onSaved={() => { setEdit(false); load(); }} />}
    </div>
  );
}

function PriceBooksPanel({ p, canEdit, reload }) {
  const [books, setBooks] = useState([]);
  const [dialog, setDialog] = useState(null); // { entry } for edit, {} for add
  useEffect(() => { api.get("/price-books").then(({ data }) => setBooks(data)); }, []);
  const entries = p.price_book_entries || [];
  const bookName = (id) => books.find((b) => b.id === id)?.name || id.slice(0, 8);
  const usedIds = new Set(entries.map((e) => e.price_book_id));
  const available = books.filter((b) => !usedIds.has(b.id));

  const remove = async (e) => {
    if (!window.confirm(`Remove ${p.name} from ${bookName(e.price_book_id)}?`)) return;
    try { await api.delete(`/products/${p.id}/price-books/${e.id}`); toast.success("Removed from price book"); reload(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div data-testid="product-pricebooks-panel">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-[#101D51]">Price book entries</h3>
        {canEdit && <Button size="sm" className="h-9 bg-[#0B7C5A] hover:bg-[#09664a]" disabled={available.length === 0}
          onClick={() => setDialog({})} data-testid="add-to-pricebook-btn"><Plus className="w-4 h-4" /> Add to price book</Button>}
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-[#6B7189]">Not in any price book yet.{canEdit && available.length > 0 ? " Use \u201cAdd to price book\u201d to set a price." : ""}</p>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[#6B7189] text-left">
            <tr><th className="py-2">Price book</th><th className="py-2 text-right">List</th><th className="py-2 text-right">Minimum</th>{canEdit && <th className="py-2 text-right">Actions</th>}</tr>
          </thead>
          <tbody>
            {entries.map((e) => (
              <tr key={e.id} className="border-t border-[#E6E8F0]" data-testid={`pricebook-entry-${e.price_book_id}`}>
                <td className="py-2 font-medium text-[#101D51]">{bookName(e.price_book_id)}</td>
                <td className="py-2 text-right font-semibold">{formatINR(e.list_price)}</td>
                <td className="py-2 text-right text-[#6B7189]">{formatINR(e.minimum_price)}</td>
                {canEdit && <td className="py-2 text-right whitespace-nowrap">
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setDialog({ entry: e })} data-testid={`edit-entry-${e.price_book_id}`}><Pencil className="w-4 h-4 text-[#6B7189]" /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => remove(e)} data-testid={`remove-entry-${e.price_book_id}`}><Trash2 className="w-4 h-4 text-[#F83A84]" /></Button>
                </td>}
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {dialog && <EntryDialog p={p} entry={dialog.entry} available={available} bookName={bookName}
        onClose={() => setDialog(null)} onSaved={() => { setDialog(null); reload(); }} />}
    </div>
  );
}

function EntryDialog({ p, entry, available, bookName, onClose, onSaved }) {
  const isEdit = !!entry;
  const [f, setF] = useState({
    price_book_id: entry?.price_book_id || available[0]?.id || "",
    list_price: (entry ? entry.list_price : p.list_price) / 100,
    minimum_price: (entry ? entry.minimum_price : p.minimum_price) / 100,
  });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!f.price_book_id) { toast.error("Pick a price book"); return; }
    setSaving(true);
    try {
      if (isEdit) await api.put(`/products/${p.id}/price-books/${entry.id}`, f);
      else await api.post(`/products/${p.id}/price-books`, f);
      toast.success(isEdit ? "Price updated" : "Added to price book");
      onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const N = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pricebook-entry-dialog">
        <DialogHeader><DialogTitle>{isEdit ? `Edit price · ${bookName(entry.price_book_id)}` : "Add to price book"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {!isEdit && (
            <L label="Price book">
              <select className="w-full h-10 rounded-md border border-[#E6E8F0] px-3 text-sm bg-white" value={f.price_book_id}
                onChange={(e) => setF({ ...f, price_book_id: e.target.value })} data-testid="entry-pricebook-select">
                {available.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </L>
          )}
          <L label="List price (₹)"><Input type="number" value={f.list_price} onChange={N("list_price")} data-testid="entry-list-price" /></L>
          <L label="Minimum price (₹)"><Input type="number" value={f.minimum_price} onChange={N("minimum_price")} data-testid="entry-minimum-price" /></L>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#101D51]" data-testid="entry-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({ p, onClose, onSaved }) {
  const [f, setF] = useState({ code: p.code, name: p.name, category: p.category, brand: p.brand || "", unit: p.unit, hsn: p.hsn || "", gst_rate: p.gst_rate,
    list_price: p.list_price / 100, minimum_price: p.minimum_price / 100, purchase_price: (p.purchase_price || 0) / 100, barcode: p.barcode || "", active: p.active });
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try { await api.put(`/products/${p.id}`, f); toast.success("Product updated"); onSaved(); } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const N = (k) => (e) => setF({ ...f, [k]: Number(e.target.value) });
  const S = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="product-edit-dialog">
        <DialogHeader><DialogTitle>Edit product</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <L label="Name"><Input value={f.name} onChange={S("name")} data-testid="edit-product-name" /></L>
          <L label="Brand"><Input value={f.brand} onChange={S("brand")} /></L>
          <L label="HSN"><Input value={f.hsn} onChange={S("hsn")} /></L>
          <L label="Barcode"><Input value={f.barcode} onChange={S("barcode")} data-testid="edit-product-barcode" /></L>
          <L label="Purchase price (₹)"><Input type="number" value={f.purchase_price} onChange={N("purchase_price")} data-testid="edit-product-purchase-price" /></L>
          <L label="List price (₹)"><Input type="number" value={f.list_price} onChange={N("list_price")} data-testid="edit-product-list-price" /></L>
          <L label="Minimum price (₹)"><Input type="number" value={f.minimum_price} onChange={N("minimum_price")} /></L>
          <L label="Margin"><div className="h-10 flex items-center text-sm font-semibold text-[#0B7C5A]">{formatINR(Math.round((f.list_price - f.purchase_price) * 100))} ({f.list_price ? (((f.list_price - f.purchase_price) / f.list_price) * 100).toFixed(1) : 0}%)</div></L>
        </div>
        <DialogFooter><Button onClick={save} disabled={saving} className="bg-[#101D51]" data-testid="edit-product-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>);
}

function L({ label, children }) {
  return <div><label className="text-xs font-medium text-[#6B7189]">{label}</label><div className="mt-1">{children}</div></div>;
}

function Row({ k, v, strong, mono, green }) {
  return (
    <div className="flex justify-between py-2 border-b border-[#E6E8F0] last:border-0">
      <span className="text-sm text-[#6B7189]">{k}</span>
      <span className={`text-sm ${strong ? "font-bold text-[#101D51]" : green ? "font-semibold text-[#0B7C5A]" : "text-[#101D51]"} ${mono ? "font-mono text-xs" : ""}`} data-testid={`product-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v}</span>
    </div>
  );
}
